import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Alert,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProviderCard from '../ProviderCard/ProviderCard';
import BusinessFilter from '../common/BusinessFilter/BusinessFilter';
import { businessService } from '../../services/api/businessService';
import { accountService } from '../../services/api/accountService';
import { navigateToStorefront } from '../../utils/storefrontPath';
import { useAuth } from '../../context/AuthContext';
import SEO from '../common/SEO/SEO';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  Container,
  HeaderBox,
  Eyebrow,
  InfoText,
  SearchField,
  NoResultsBox,
  LoaderBox,
  BusinessCard,
  HeadingText,
  PaginationContainer,
  ProviderCountText,
  SearchRow,
  ServicesButton,
} from './OurPartners.styled';

const OurPartners = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    serviceTypes: [],
  });
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [savedBusinessIds, setSavedBusinessIds] = useState([]);
  const savedBusinessIdsRef = useRef([]);
  const { user } = useAuth();
  const prevUserRef = useRef(user);
  const navigate = useNavigate();

  const PROVIDERS_PER_PAGE = 12;

  useEffect(() => {
    savedBusinessIdsRef.current = savedBusinessIds;
  }, [savedBusinessIds]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const data = await businessService.getAllBusinesses(true);
        const uniqueProviders = Array.from(
          new Map(data.map((item) => [item.BusinessId, item])).values(),
        );
        setProviders(uniqueProviders);
      } catch (err) {
        console.error('Error fetching businesses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchSavedIds = async () => {
      if (!user) {
        setSavedBusinessIds([]);
        savedBusinessIdsRef.current = [];
        return;
      }

      try {
        const data = await accountService.getSavedBusinesses();
        const ids = Array.isArray(data) ? data : [];
        if (!cancelled) {
          setSavedBusinessIds(ids);
          savedBusinessIdsRef.current = ids;
        }
      } catch (err) {
        console.error('Error fetching saved businesses:', err);
        if (!cancelled) {
          setSavedBusinessIds([]);
          savedBusinessIdsRef.current = [];
        }
      }
    };

    fetchSavedIds();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Reset filters and search only when a signed-in user logs out (not for guests).
  useEffect(() => {
    const wasLoggedIn = Boolean(prevUserRef.current);
    prevUserRef.current = user;
    if (wasLoggedIn && !user) {
      setFilters({
        serviceTypes: [],
      });
      setFavoritesOnly(false);
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [user]);

  const handleToggleSaved = useCallback(async (businessId) => {
    const previousIds = savedBusinessIdsRef.current;
    const nextIds = previousIds.includes(businessId)
      ? previousIds.filter((id) => id !== businessId)
      : [...previousIds, businessId];

    setSavedBusinessIds(nextIds);
    savedBusinessIdsRef.current = nextIds;

    try {
      await accountService.editSavedBusinesses(nextIds);
    } catch (err) {
      console.error('Error saving favorite partner:', err);
      setSavedBusinessIds(previousIds);
      savedBusinessIdsRef.current = previousIds;
    }
  }, []);

  // Filter providers by search term, service type, and favorites
  const filteredProviders = providers.filter((provider) => {
    // Search term filter
    const name = provider.BusinessName || '';
    const email = provider.BusinessEmail || '';
    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Service type filter — trade-level (matches `Service.ServiceType` / `ALL_TRADES.id`)
    if (filters.serviceTypes.length > 0) {
      const providerTradeIds = new Set(
        (provider.Services || [])
          .map((s) => s.ServiceType)
          .filter(Boolean),
      );
      const hasMatchingTrade = filters.serviceTypes.some((selected) =>
        providerTradeIds.has(selected.value),
      );
      if (!hasMatchingTrade) return false;
    }

    if (favoritesOnly && !savedBusinessIds.includes(provider.BusinessId)) {
      return false;
    }

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProviders.length / PROVIDERS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROVIDERS_PER_PAGE;
  const endIndex = startIndex + PROVIDERS_PER_PAGE;
  const paginatedProviders = filteredProviders.slice(startIndex, endIndex);

  const filterServiceTypeKey = filters.serviceTypes
    .map((s) => s.value)
    .sort()
    .join(',');

  // Reset to first page when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterServiceTypeKey, favoritesOnly]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (business) => {
    navigateToStorefront(navigate, business);
  };

  const handleServiceClick = () => {
    navigate('/services');
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleFavoritesOnlyChange = useCallback((nextValue) => {
    setFavoritesOnly(nextValue);
  }, []);

  if (loading) {
    return (
      <>
        <SEO
          title="Partners"
          description="Discover vetted local home service businesses in Northern Kentucky. Browse trusted partners on NKY Connect."
          path="/partners"
        />
        <LoaderBox>
          <NkySpinner />
        </LoaderBox>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO
          title="Partners"
          description="Discover vetted local home service businesses in Northern Kentucky. Browse trusted partners on NKY Connect."
          path="/partners"
        />
        <Container>
          <Alert severity="error">{error}</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Partners"
        description="Discover vetted local home service businesses in Northern Kentucky. Browse trusted partners on NKY Connect."
        path="/partners"
      />
      <Container>
      <HeaderBox aria-labelledby="partners-title">
        <Eyebrow>Browse pros</Eyebrow>
        <HeadingText id="partners-title">Discover trusted local pros</HeadingText>
        <InfoText>
          Every partner is a vetted local business based in Northern Kentucky,
          ready to help when you need them.
        </InfoText>

        <SearchRow>
          <SearchField>
            <SearchIcon aria-hidden />
            <input
              type="search"
              placeholder="Search by business name or email"
              aria-label="Search businesses"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchField>
          <ServicesButton onClick={handleServiceClick}>
            Search by job instead
          </ServicesButton>
        </SearchRow>

        <BusinessFilter
          key={user?.AccountId ?? 'guest'}
          onFilterChange={handleFilterChange}
          businesses={providers}
          showPriceFilter={false}
          showFavoritesFilter
          favoritesOnly={favoritesOnly}
          onFavoritesOnlyChange={handleFavoritesOnlyChange}
          requireAuthForFilters={false}
        />
      </HeaderBox>

      <ProviderCountText>
        Showing {filteredProviders.length}
        {favoritesOnly ? ' favorite' : ' trusted'} provider
        {filteredProviders.length !== 1 ? 's' : ''}
        {totalPages > 1 && (
          <span>
            {' '}
            (Page {currentPage} of {totalPages})
          </span>
        )}
      </ProviderCountText>

      <Grid container spacing={3}>
        {paginatedProviders.map((provider) => {
          const city = provider.BusinessAddress?.City;
          const state = provider.BusinessAddress?.State;
          const locationDisplay =
            city && state ? `${city}, ${state}` : 'Northern Kentucky';

          return (
            <Grid item xs={12} sm={6} md={4} key={provider.BusinessId}>
              <BusinessCard>
                <ProviderCard
                  provider={provider}
                  locationDisplay={locationDisplay}
                  isSaved={savedBusinessIds.includes(provider.BusinessId)}
                  onToggleSaved={handleToggleSaved}
                  onClick={() => handleCardClick(provider)}
                />
              </BusinessCard>
            </Grid>
          );
        })}
      </Grid>

      {filteredProviders.length === 0 && (
        <NoResultsBox>
          <Typography component="p">
            {favoritesOnly
              ? 'No favorite partners match your filters. Star a partner to save them here.'
              : searchTerm
                ? 'No businesses found matching your search.'
                : 'No businesses available at the moment.'}
          </Typography>
        </NoResultsBox>
      )}

      {totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </PaginationContainer>
      )}
    </Container>
    </>
  );
};

export default OurPartners;
